<template>
    <div class="chart-container">
    <div v-if="size !== 'mini' && selectedDistResult !== null" class="chart-legend-container" style="height: 60px;">
      <label class="chart-legend-wrapper" v-for="(result, index) in selectedDistResult.value" :key="result.name">
        <span class="chart-mark chart-mark-count"
          :style="{ backgroundColor: getGraphColor(index).borderColor }"></span>
        <span>{{getLabel(result.name)}}</span>
        <el-switch class="chart-legend-switch" v-model="switches[index]"></el-switch>
      </label>
    </div>
    <div class="chart-main-container">
      <chart ref="chart" chartType="bar" chartId="ClusterTargetChart" :width="size === 'mini' ? 300 : 836"
             :height="size === 'mini' ? 180 : 400"></chart>
    </div>

    <div class="column-select-container" v-if="this.size !== 'mini' && selectedDistResult">
      <el-radio v-for="(value, index) in clusterDistResults" v-model="selectedColumnIndex"
                :key="index" name="cluster-column" :label="index">{{ value.name }}
      </el-radio>
    </div>
  </div>
</template>

<script lang="js">
import ChartCmp from './base.vue'
import CommonUtil from '../../utils/common'
import FormatUtil from '../../utils/format'
import { GraphLineColorSets } from '../../defines/chart-color'

export default {
  name: 'ClusterDistributionChartCmp',
  components: {
    'chart': ChartCmp
  },
  props: {
    clusterDistributionComparison: {type: Object, default: () => {}},
    yName: '',
    categoryId: '',
    size: ''
  },
  data () {
    return {
      switches: [],
      clusterDistResults: this.clusterDistributionComparison.results,
      selectedDistResult: null,
      selectedColumnIndex: -1,
      yLabel: '割合',
      xLabel: '項目の値',
      lineColors: GraphLineColorSets
    }
  },
  created () {
    let tmpSwitches = Array(this.clusterDistResults[0].value.length)
    for (let index = 0; index < tmpSwitches.length; index++) {
      tmpSwitches[index] = true
    }
    this.switches = tmpSwitches
    this.selectedColumnIndex = 0
    this.$nextTick(() => {
      this.$forceUpdate()
    })
  },
  watch: {
    switches (newVal) {
      const chart = this.$refs.chart
      if (CommonUtil.isNull(chart)) return

      for (let index in newVal) {
        if (!chart.nativeChart) continue
        const meta = chart.getDatasetMeta(Number(index))
        meta.hidden = !newVal[index]
        chart.update()
      }
    },
    selectedColumnIndex (newIndex) {
      this.switchDisplay(newIndex)
    },
    selectedDistResult (distData) {
      const isMini = (this.size === 'mini')

      if (CommonUtil.isEmpty(distData)) return

      let datasets = []

      distData.value.forEach((value, index) => {
        let dataset = {
          type: 'line',
          label: this.getLabel(value.name),
          data: value.value,
          yAxisID: 'ratio',
          fill: false,
          pointBorderWidth: isMini ? 4 : 8,
          lineTension: 0,
          hidden: !this.switches[Number(index)]
        }
        if (value.name === 'all') {
          dataset = Object.assign(dataset, {
            borderDash: [5, 5],
            pointStyle: 'rect'
          })
        }

        dataset = Object.assign(dataset, this.getGraphColor(index))
        datasets.push(dataset)
      })

      const chartData = {
        labels: distData.order,
        datasets: datasets
      }

      const options = {
        responsive: isMini,
        title: {
          display: false
        },
        legend: {
          display: false
        },
        scales: {
          yAxes: [{
            id: 'ratio',
            type: 'linear',
            position: 'left',
            scaleLabel: {
              display: !isMini,
              labelString: this.yLabel,
              fontSize: 16
            },
            ticks: {
              beginAtZero: true,
              min: 0,
              max: 1,
              stepSize: isMini ? 0.2 : 0.1,
              major: {},
              minor: {},
              callback: function (value, index, values) {
                return CommonUtil.isEmpty(value) ? '' : `${FormatUtil.percentage(value)}%`
              }
            }
          }],
          xAxes: [
            {
              display: !isMini,
              scaleLabel: {
                labelString: this.xLabel,
                fontSize: 16
              },
              ticks: {
                beginAtZero: false,
                major: {},
                minor: {},
                maxRotation: 90,
                callback: function (value, index, values) {
                  if (!isMini && CommonUtil.isString(value) && CommonUtil.lengthBytes(value) > 20) {
                    value = CommonUtil.substringBytes(value, 20) + '…'
                  }
                  return value
                }
              }
            }
          ]
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              const datasetIndex = tooltipItem.datasetIndex
              let label = data.datasets[datasetIndex].label || ''
              const value = CommonUtil.isEmpty(tooltipItem.yLabel) ? '' : `${FormatUtil.percentage(tooltipItem.yLabel, 2)}%`
              return `${label}: ${value}`
            }
          }
        }
      }

      this.$nextTick(() => {
        this.$refs.chart.renderChart(chartData, options)
        this.$forceUpdate()
      })
    }
  },
  methods: {
    switchDisplay (columnIndex) {
      if (columnIndex > -1) {
        this.selectedDistResult = this.clusterDistResults[columnIndex]
      }
    },
    getGraphColor (index) {
      const colorIndex = index % this.lineColors.length
      return this.lineColors[colorIndex]
    },
    getLabel (rawData) {
      return rawData === 'all' ? '全データ' : rawData
    }
  }
}

</script>

<style scoped>
  .chart-container {
    display: flex;
    flex-direction: column;
  }

  .chart-legend-container {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    overflow-y: auto;
  }

  .chart-legend-container > label {
    display: flex;
    align-items: center;
  }

  .chart-mark {
    display: inline-block;
    width: 12px;
    height: 12px;
  }

  .chart-mark-count {
    background-color: #86C7F5;
    margin-right: 10px;
  }

  .chart-legend-switch {
    transform: scale(0.6, 0.6);
  }

  .chart-legend-wrapper {
    margin-right: 10px;
  }

  .chart-main-container {
    display: flex;
    justify-content: center;
  }

  .column-select-container {
    margin-top: 20px;
    overflow-x: scroll;
    overflow-y: hidden;
    display: flex;
    flex-wrap: nowrap;
    flex-shrink: 0;
    padding: 0 24px;
  }

  .column-select-container > * {
    margin: 12px 6px;
  }
</style>



// WEBPACK FOOTER //
// src/components/chart/cluster-distribution.vue